import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface CodeBlockProps {
  code: string;
  language?: string;
  showLineNumbers?: boolean;
}

/**
 * CodeBlock component with syntax highlighting and copy button
 * Uses react-syntax-highlighter with VS Code Dark+ theme
 */
export default function CodeBlock({
  code,
  language = 'text',
  showLineNumbers = false
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Normalize language name
  const normalizedLang = normalizeLanguage(language);

  return (
    <div className="code-block-wrapper relative group my-3">
      {/* Header with language label and copy button */}
      <div className="code-block-header flex items-center justify-between px-4 py-2 bg-[#1e1e1e] border-b border-[#333] rounded-t-lg">
        <span className="text-xs text-gray-400 font-mono uppercase">
          {normalizedLang || 'code'}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-[#333] rounded transition-colors"
          title="Copy code"
        >
          {copied ? (
            <>
              <CheckIcon className="w-4 h-4 text-green-400" />
              <span className="text-green-400">Copied!</span>
            </>
          ) : (
            <>
              <CopyIcon className="w-4 h-4" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code content with syntax highlighting */}
      {/* For plain text/ASCII art, use simple <pre> to preserve whitespace exactly */}
      {normalizedLang === 'text' ? (
        <pre
          style={{
            margin: 0,
            borderRadius: '0 0 0.5rem 0.5rem',
            padding: '1rem',
            fontSize: '0.875rem',
            lineHeight: '1.5',
            backgroundColor: '#1e1e1e',
            color: '#d4d4d4',
            fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
            whiteSpace: 'pre',
            overflowX: 'auto',
          }}
        >
          {code}
        </pre>
      ) : (
        <SyntaxHighlighter
          language={normalizedLang}
          style={vscDarkPlus}
          showLineNumbers={showLineNumbers}
          wrapLines={true}
          customStyle={{
            margin: 0,
            borderRadius: '0 0 0.5rem 0.5rem',
            padding: '1rem',
            fontSize: '0.875rem',
            lineHeight: '1.5',
            backgroundColor: '#1e1e1e',
          }}
          codeTagProps={{
            style: {
              fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
            }
          }}
        >
          {code.trim()}
        </SyntaxHighlighter>
      )}
    </div>
  );
}

/**
 * Normalize language names for syntax highlighter
 */
function normalizeLanguage(lang: string): string {
  const langMap: Record<string, string> = {
    'js': 'javascript',
    'ts': 'typescript',
    'py': 'python',
    'rb': 'ruby',
    'sh': 'bash',
    'shell': 'bash',
    'zsh': 'bash',
    'yml': 'yaml',
    'md': 'markdown',
    'json5': 'json',
    'jsonc': 'json',
    'dockerfile': 'docker',
    'plaintext': 'text',
    'txt': 'text',
    '': 'text',
  };

  const normalized = lang.toLowerCase().trim();
  return langMap[normalized] || normalized;
}

// Simple copy icon
function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

// Simple check icon
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
